import { useEffect, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { closeSnackbar, enqueueSnackbar } from 'notistack';

import { useGetNotifications } from '@/entities/notifications';
import { getProjectPath } from '@/entities/project';

import { NOTIFICATION_EVENT } from '@/shared/api/notifications/types';
import { TAB_PARAM_LIP_SYNC_VALUE, TAB_PARAM_NAME } from '@/shared/constants/routes/searchParams';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import { NOTIFICATION_EVENT_MESSAGES } from '../lib/categories';
import { ReadyNotificationContent } from '../ui/ReadyNotificationContent';

export const useReadyNotificationsListener = (enabled: boolean) => {
  const navigate = useNavigate();
  const notifiedKeys = useRef(new Set<string>());

  const { data } = useGetNotifications(enabled);

  useEffect(() => {
    if (!enabled || !data) {
      return;
    }

    data.forEach((notification) => {
      const message = NOTIFICATION_EVENT_MESSAGES[notification.event_name];

      if (!message) {
        return;
      }

      const key = `${notification.event_name}:${notification.project_id}`;

      if (notifiedKeys.current.has(key)) {
        return;
      }

      notifiedKeys.current.add(key);

      if (notification.event_name === NOTIFICATION_EVENT.LipSyncDone) {
        MixpanelEvents.lipsyncReadyNotificationShown({ project_id: notification.project_id });
      }

      const snackbarKey = enqueueSnackbar(
        <ReadyNotificationContent
          message={message}
          onOpen={() => {
            if (notification.event_name === NOTIFICATION_EVENT.LipSyncDone) {
              MixpanelEvents.lipsyncReadyNotificationClicked({ project_id: notification.project_id });
            }

            const path = getProjectPath(notification.project_id, notification.space_id, true);

            if (path) {
              const isLipSyncDone = notification.event_name === NOTIFICATION_EVENT.LipSyncDone;
              const destination = isLipSyncDone
                ? `${path}?${TAB_PARAM_NAME}=${TAB_PARAM_LIP_SYNC_VALUE}`
                : path;

              navigate(destination);
            }

            closeSnackbar(snackbarKey);
          }}
        />,
        {
          persist: true,
          variant: 'success',
        },
      );
    });
  }, [enabled, data, navigate]);
};
